import React from 'react';

export interface ExifStatus {
    id?: number;
    current_step?: string;
    processed_files: number;
    total_files: number;
    is_generating: boolean;
    last_status_message?: string;
    updated_at?: string;
}

interface ExifMonitoringModalProps {
    status: ExifStatus | null;
    onClose: () => void;
}

const STEPS = [
    { key: 'INIT', label: 'Inizializzazione' },
    { key: 'EXTRACT_TIFF', label: 'Estrazione dati file TIFF' },
    { key: 'SAVE_TIFF', label: 'Salvataggio exif_tif.xlsx' },
    { key: 'EXTRACT_JPEG', label: 'Estrazione dati file JPEG' },
    { key: 'SAVE_JPEG', label: 'Salvataggio exif_jpg.xlsx' },
];

const STEP_ORDER = STEPS.map(s => s.key);

function getStepState(stepKey: string, currentStep: string | undefined, isGenerating: boolean): 'pending' | 'active' | 'done' | 'error' {
    if (!currentStep) return 'pending';

    if (currentStep === 'COMPLETED') return 'done';

    if (currentStep === 'ERROR') return 'error';

    const currentIdx = STEP_ORDER.indexOf(currentStep);
    const thisIdx = STEP_ORDER.indexOf(stepKey);

    if (currentIdx === -1) return 'pending';

    if (thisIdx < currentIdx) return 'done';
    if (thisIdx === currentIdx) {
        if (isGenerating) return 'active';
        return 'error';
    }
    return 'pending';
}

const ExifMonitoringModal: React.FC<ExifMonitoringModalProps> = ({ status, onClose }) => {
    if (!status) return null;

    const currentStep = status.current_step;
    const isGenerating = status.is_generating;
    const isError = currentStep === 'ERROR';
    const isCompleted = currentStep === 'COMPLETED';

    const progressPct = status.total_files > 0
        ? Math.round((status.processed_files / status.total_files) * 100)
        : 0;

    return (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal-content" style={{ maxWidth: '520px', padding: '32px' }}>
                <h2 style={{ marginTop: 0, marginBottom: '24px', fontSize: '1.3rem' }}>
                    {isCompleted ? '✅ Estrazione completata' : isError ? '❌ Errore estrazione' : '⚙️ Estrazione EXIF in corso...'}
                </h2>

                <div style={{ marginBottom: '24px' }}>
                    {STEPS.map(step => {
                        const state = getStepState(step.key, currentStep, isGenerating);
                        const isExtractStep = step.key === 'EXTRACT_TIFF' || step.key === 'EXTRACT_JPEG';
                        const isActiveExtract = isExtractStep && currentStep === step.key && status.total_files > 0;

                        return (
                            <div key={step.key} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '8px 0',
                                borderBottom: '1px solid rgba(0,0,0,0.06)',
                                opacity: state === 'pending' ? 0.4 : 1,
                                transition: 'opacity 0.3s ease',
                            }}>
                                <div style={{ width: '24px', textAlign: 'center', flexShrink: 0 }}>
                                    {state === 'done' && <span style={{ color: '#22c55e', fontSize: '1.1rem' }}>✓</span>}
                                    {state === 'active' && (
                                        <span style={{
                                            display: 'inline-block',
                                            width: '14px',
                                            height: '14px',
                                            border: '2px solid var(--primary-color)',
                                            borderTopColor: 'transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 0.8s linear infinite',
                                        }} />
                                    )}
                                    {state === 'error' && <span style={{ color: '#ef4444', fontSize: '1.1rem' }}>✗</span>}
                                    {state === 'pending' && <span style={{ color: '#ccc', fontSize: '1rem' }}>○</span>}
                                </div>

                                <span style={{
                                    flex: 1,
                                    fontWeight: state === 'active' ? 600 : 400,
                                    color: state === 'error' ? '#ef4444' : state === 'done' ? '#22c55e' : 'inherit',
                                }}>
                                    {step.label}
                                </span>

                                {isActiveExtract && (
                                    <span style={{
                                        fontSize: '0.8rem',
                                        color: 'var(--primary-color)',
                                        fontWeight: 600,
                                        background: 'rgba(var(--primary-rgb, 99,102,241), 0.1)',
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {status.processed_files} / {status.total_files}
                                    </span>
                                )}
                                {isExtractStep && state === 'done' && status.total_files > 0 && (
                                    <span style={{ fontSize: '0.8rem', color: '#22c55e', fontWeight: 600 }}>
                                        {status.total_files} file
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>

                {status.total_files > 0 && (currentStep === 'EXTRACT_TIFF' || currentStep === 'EXTRACT_JPEG') && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '4px', color: '#666' }}>
                            <span>Avanzamento estrazione dati</span>
                            <span>{status.processed_files} / {status.total_files} ({progressPct}%)</span>
                        </div>
                        <div style={{ height: '6px', background: '#eee', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{
                                height: '100%',
                                width: `${progressPct}%`,
                                background: 'var(--primary-color)',
                                borderRadius: '3px',
                                transition: 'width 0.4s ease',
                            }} />
                        </div>
                    </div>
                )}

                {isError && (
                    <div style={{
                        padding: '16px',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #f87171',
                        borderRadius: '6px',
                        color: '#b91c1c',
                        marginBottom: '24px'
                    }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Dettagli Errore:</h4>
                        <p style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '13px' }}>
                            {status.last_status_message || "Errore sconosciuto durante l'estrazione degli EXIF."}
                        </p>
                    </div>
                )}
                {!isError && status.last_status_message && (
                    <div style={{
                        fontSize: '0.85rem',
                        color: '#666',
                        background: '#f8f9fa',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        marginBottom: '20px',
                        fontFamily: 'monospace',
                    }}>
                        {status.last_status_message}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <button
                        className="btn btn-primary"
                        onClick={onClose}
                        disabled={isGenerating && !isError && !isCompleted}
                    >
                        {isGenerating && !isError && !isCompleted ? 'Estrazione in corso...' : 'CHIUDI'}
                    </button>
                </div>
            </div>

            <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
        </div>
    );
};

export default ExifMonitoringModal;
